/**
 * BeHeard's transport for Central billing/settings calls, shaped as the shared
 * `CentralBillingFetcher` contract (@levelup/central-billing 1.4.0):
 *
 *   (path, init?) => Promise<{ status, body }>
 *
 * Browser-direct with the Central access token BeHeard already holds. Every
 * non-2xx (and a transport failure) goes through the SAME registered
 * classifier as centralBillingApi.ts (applyRegisteredCentralFailure): a dead
 * session, an expired token (silent renewal), a closed workspace or a revoked
 * membership is acted on there and reported back as `handled: true`, so the
 * caller renders nothing over it. Central's status and body (machine `code`)
 * are relayed unchanged; nothing throws for an HTTP answer; no token is stored.
 */
import { CENTRAL_URL } from '../config/centralAuth';
import { applyRegisteredCentralFailure, terminalForCaller } from '../auth/reportCentralFailure';

export interface CentralBillingFetchInit {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
  signal?: AbortSignal;
  /** Extra headers (never a token) — the shared CentralFetchInit allows them. */
  headers?: Record<string, string>;
}

export interface BeHeardCentralResponse {
  status: number;
  body: unknown;
  handled: boolean;
}

export type BeHeardCentralFetcher = (path: string, init?: CentralBillingFetchInit) => Promise<BeHeardCentralResponse>;

export function createBeHeardCentralFetcher(
  getAccessToken: () => string | null,
  centralUrl: string = CENTRAL_URL,
): BeHeardCentralFetcher {
  return async (path, init = {}) => {
    const token = getAccessToken();
    const base = (centralUrl || '').replace(/\/+$/, '');
    if (!base || !token) return { status: 0, body: null, handled: false };
    const method = init.method ?? 'GET';
    const headers: Record<string, string> = { ...(init.headers ?? {}), Authorization: `Bearer ${token}`, Accept: 'application/json' };
    let body: string | undefined;
    if (init.body !== undefined && method !== 'GET') {
      headers['Content-Type'] = 'application/json';
      body = JSON.stringify(init.body);
    }
    let res: Response;
    try {
      res = await fetch(`${base}${path}`, { method, headers, body, signal: init.signal });
    } catch (err) {
      if ((err as { name?: string } | null)?.name === 'AbortError') throw err;
      const action = applyRegisteredCentralFailure({ status: 'FETCH_ERROR', source: `centralBilling${path}` });
      return { status: 0, body: null, handled: action !== null && terminalForCaller(action) };
    }
    let parsed: unknown = null;
    try {
      const text = await res.text();
      parsed = text ? JSON.parse(text) : null;
    } catch {
      parsed = null;
    }
    if (res.ok) return { status: res.status, body: parsed, handled: false };
    const action = applyRegisteredCentralFailure({ status: res.status, body: parsed ?? undefined, source: `centralBilling${path}` });
    return { status: res.status, body: parsed, handled: action !== null && terminalForCaller(action) };
  };
}
